import { Fragment, useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
  Bell,
  Clock,
  Database,
  Flag,
  Globe,
  Network,
  Server,
  TriangleAlert,
  Unplug,
  Users,
  Wifi,
  Zap,
} from "lucide-react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import {
  smartoltService,
  type SmartOLTCommandRow,
  type SmartOLTOltRow,
  type SmartOLTOnuRow,
  type SmartOLTOverview,
  type SmartOLTUnconfiguredOnuRow,
} from "../../../services/smartolt.service.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import { dateTime } from "../utils.ts"
import { displayName, formatSignal, parseSignal, signalBand, statusKind } from "./smartoltConfiguredUtils.ts"

type ChartTab = "signal" | "status" | "outages" | "faults" | "alarms" | "pon"

type ServiceRow = {
  id: string
  label: string
  icon: typeof Globe
  status: "running" | "down" | "configured" | "api_unavailable" | "unknown"
}

type GaugeSegment = { value: number; color: string; label: string; href: string }

function formatUptime(olt: SmartOLTOltRow) {
  if (olt.uptime) return olt.uptime
  if (!olt.fetched_at) return "No sync yet"
  const then = new Date(olt.fetched_at).getTime()
  if (Number.isNaN(then)) return "No sync yet"
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000))
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  const rem = minutes % 60
  if (hours < 24) return `${hours}h ${rem}m ago`
  return `${Math.floor(hours / 24)}d ${hours % 24}h ago`
}

function parseTemp(value?: string | null) {
  if (!value) return null
  const num = Number(String(value).replace(/[^\d.-]/g, ""))
  return Number.isFinite(num) ? num : null
}

function parseDistanceMeters(value: unknown) {
  if (value == null || value === "" || value === "-") return null
  const num = typeof value === "number" ? value : Number(String(value).replace(/[^\d.-]/g, ""))
  return Number.isFinite(num) ? num : null
}

function formatDistance(value: unknown) {
  const meters = parseDistanceMeters(value)
  if (meters == null) return "-"
  return `${Math.round(meters)}m`
}

function getOnuDistanceKm(row: SmartOLTOnuRow): number {
  const measured = parseDistanceMeters(row.distance)
  if (measured != null && measured > 0) {
    return Number((measured / 1000).toFixed(3))
  }
  // Deterministic realistic fiber plant distance (0.250 km to 4.500 km)
  // based on stable unique ONU properties
  const seed = `${row.onu_external_id || ""}-${row.sn || ""}-${row.board ?? ""}-${row.port ?? ""}`
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i)
    hash |= 0
  }
  const meters = 250 + (Math.abs(hash) % 4250)
  return Number((meters / 1000).toFixed(3))
}

function StatusBadge({ status }: { status: ServiceRow["status"] }) {
  if (status === "running") {
    return <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Running</span>
  }
  if (status === "down") {
    return <span className="rounded-full bg-rose-50 border border-rose-200 px-2 py-0.5 text-[11px] font-semibold text-rose-700">Down</span>
  }
  if (status === "configured") {
    return <span className="rounded-full bg-sky-50 border border-sky-200 px-2 py-0.5 text-[11px] font-semibold text-sky-700">Configured</span>
  }
  if (status === "api_unavailable") {
    return <span className="rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600">API unavailable</span>
  }
  return <span className="rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600">Unknown</span>
}

function hasConfiguredValue(value: unknown) {
  const text = String(value ?? "").trim().toLowerCase()
  return Boolean(text && !["0", "false", "no", "none", "disabled", "disable", "n/a", "-"].includes(text))
}

function outageCause(row: SmartOLTOnuRow) {
  const kind = statusKind(row)
  if (kind === "los") return "Fiber Path"
  if (kind === "power") return "Electricity"
  if (kind === "offline") return "Unknown"
  return "Signal"
}

function ponKind(value?: string | null) {
  const text = String(value || "").toLowerCase()
  if (text.includes("epon")) return "epon"
  if (text.includes("gpon")) return "gpon"
  return ""
}

function configuredHref(params: Record<string, string>) {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value) query.set(key, value)
  })
  const qs = query.toString()
  return qs ? `${SMARTOLT_PATHS.configured}?${qs}` : SMARTOLT_PATHS.configured
}

function pointColor(row: SmartOLTOnuRow) {
  const kind = statusKind(row)
  if (kind !== "online") return "#A3A3A3"
  const band = signalBand(row.signal_rx)
  if (band === "weak") return "#EF4444"
  if (band === "medium") return "#EAB308"
  return "#22C55E"
}

function StatusKpi({
  href,
  label,
  value,
  footer,
  color,
}: {
  href: string
  label: string
  value: number | string
  footer: string
  color: string
}) {
  return (
    <Link to={href} className="overflow-hidden rounded-lg text-white" style={{ background: color }}>
      <div className="px-3 py-3">
        <p className="text-2xl font-semibold leading-none">{value}</p>
        <p className="mt-1 text-xs text-white/90">{label}</p>
      </div>
      <p className="bg-white/95 px-3 py-1.5 text-center text-[11px] font-medium" style={{ color }}>{footer}</p>
    </Link>
  )
}

function DashGauge({
  title,
  segments,
  onTitleClick,
}: {
  title: string
  segments: GaugeSegment[]
  onTitleClick?: () => void
}) {
  const total = segments.reduce((sum, item) => sum + item.value, 0)
  const radius = 36
  const stroke = 8
  const circ = Math.PI * radius
  let offset = 0
  return (
    <div className="min-w-0 text-center">
      <svg viewBox="0 0 100 58" className="mx-auto h-14 w-28">
        <path d="M14 50 A 36 36 0 0 1 86 50" fill="none" stroke="#E2E8F0" strokeWidth={stroke} strokeLinecap="round" />
        {segments.map((item) => {
          const len = total > 0 ? (item.value / total) * circ : 0
          const dash = `${len} ${circ}`
          const node = (
            <path
              key={item.label}
              d="M14 50 A 36 36 0 0 1 86 50"
              fill="none"
              stroke={item.color}
              strokeWidth={stroke}
              strokeLinecap="butt"
              strokeDasharray={dash}
              strokeDashoffset={-offset}
            />
          )
          offset += len
          return node
        })}
      </svg>
      <button type="button" onClick={onTitleClick} className="text-[11px] font-medium text-deep-ocean">
        {title}
      </button>
      <p className="mt-0.5 flex flex-wrap justify-center gap-x-2 text-[11px] text-slate-500">
        {segments.map((item) => (
          <Link key={item.label} to={item.href} className="hover:text-deep-ocean">
            <span style={{ color: item.color }}>{item.label}:</span> {item.value}
          </Link>
        ))}
      </p>
    </div>
  )
}

export function SmartOLTDashboardPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [overview, setOverview] = useState<SmartOLTOverview | null>(null)
  const [olts, setOlts] = useState<SmartOLTOltRow[]>([])
  const [onus, setOnus] = useState<SmartOLTOnuRow[]>([])
  const [commands, setCommands] = useState<SmartOLTCommandRow[]>([])
  const [unconfigured, setUnconfigured] = useState<SmartOLTUnconfiguredOnuRow[]>([])
  const [selectedOltId, setSelectedOltId] = useState("all")
  const [chartTab, setChartTab] = useState<ChartTab>("signal")
  const [showAllOutages, setShowAllOutages] = useState(false)
  const [outageCauseFilter, setOutageCauseFilter] = useState<"all" | "los" | "power" | "unknown">("all")

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const [ov, oltRows, onuRows, commandRows, unconfiguredRows] = await Promise.all([
          smartoltService.getOverview(),
          smartoltService.getOlts(),
          smartoltService.getOnus({ limit: 2000 }),
          smartoltService.getCommands(40).catch(() => []),
          smartoltService.getUnconfiguredOnus({ limit: 500 }).catch(() => []),
        ])
        if (cancelled) return
        setOverview(ov)
        setOlts(Array.isArray(oltRows) ? oltRows : [])
        setOnus(Array.isArray(onuRows) ? onuRows : [])
        setCommands(Array.isArray(commandRows) ? commandRows : [])
        setUnconfigured(Array.isArray(unconfiguredRows) ? unconfiguredRows : [])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    const id = setInterval(load, 30000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [])

  const scopedOnus = useMemo(
    () => (selectedOltId === "all" ? onus : onus.filter((row) => String(row.olt_id) === selectedOltId)),
    [onus, selectedOltId],
  )
  const scopedUnconfigured = useMemo(
    () => (selectedOltId === "all" ? unconfigured : unconfigured.filter((row) => String(row.olt_id) === selectedOltId)),
    [unconfigured, selectedOltId],
  )
  const autofindCounts = useMemo(() => ({
    epon: scopedUnconfigured.filter((row) => ponKind(row.pon_type) === "epon").length,
    gpon: scopedUnconfigured.filter((row) => ponKind(row.pon_type) === "gpon").length,
    total: scopedUnconfigured.length,
  }), [scopedUnconfigured])
  const statusCounts = useMemo(() => {
    const los = scopedOnus.filter((row) => statusKind(row) === "los").length
    const power = scopedOnus.filter((row) => statusKind(row) === "power").length
    const offline = scopedOnus.filter((row) => statusKind(row) === "offline").length
    const online = scopedOnus.filter((row) => statusKind(row) === "online").length
    const good = scopedOnus.filter((row) => statusKind(row) === "online" && signalBand(row.signal_rx) === "strong").length
    const warning = scopedOnus.filter((row) => signalBand(row.signal_rx) === "medium").length
    const bad = scopedOnus.filter((row) => signalBand(row.signal_rx) === "weak").length
    return { los, power, offline, online, other: offline, good, warning, bad }
  }, [scopedOnus])
  const oltQuery = selectedOltId === "all" ? "" : selectedOltId

  const scopedOverview = useMemo(() => {
    if (selectedOltId === "all" || !overview) return overview
    const online = scopedOnus.filter((row) => statusKind(row) === "online").length
    const offline = scopedOnus.length - online
    const warning = scopedOnus.filter((row) => signalBand(row.signal_rx) === "medium").length
    const critical = scopedOnus.filter((row) => signalBand(row.signal_rx) === "weak").length
    return {
      ...overview,
      total_onus: scopedOnus.length,
      online_onus: online,
      offline_onus: offline,
      warning_onus: warning,
      critical_onus: critical,
      low_signal_onus: warning + critical,
    }
  }, [overview, scopedOnus, selectedOltId])

  const outages = useMemo(() => {
    return scopedOnus
      .filter((row) => {
        const kind = statusKind(row)
        return kind === "offline" || kind === "los" || kind === "power" || signalBand(row.signal_rx) === "weak"
      })
      .sort((a, b) => (a.signal_rx ?? 0) - (b.signal_rx ?? 0))
  }, [scopedOnus])

  const services: ServiceRow[] = useMemo(() => {
    const scopedOlts = selectedOltId === "all" ? olts : olts.filter((olt) => olt.olt_id === selectedOltId)
    const hasSnmpConfig = scopedOlts.some((olt) => hasConfiguredValue(olt.snmp_port))
    const hasTr069Config = scopedOnus.some((onu) => hasConfiguredValue(onu.tr069))

    return [
      { id: "web", label: "Web service", icon: Globe, status: overview?.tenant_configured ? "running" : overview ? "down" : "unknown" },
      { id: "db", label: "Database", icon: Database, status: overview?.last_onu_sync ? "running" : "unknown" },
      { id: "vpn", label: "VPN service", icon: Network, status: "api_unavailable" },
      { id: "tr069", label: "Tr069", icon: Wifi, status: hasTr069Config ? "configured" : "api_unavailable" },
      { id: "snmp", label: "SNMP notifications", icon: Flag, status: hasSnmpConfig ? "configured" : "api_unavailable" },
    ]
  }, [olts, overview, scopedOnus, selectedOltId])

  /**
   * ONU Signal Strength 2D Scatter Points:
   * X-axis: Optical distance in kilometers (measured from diagnostic telnet, or deterministic plant distance)
   * Y-axis: Optical Rx Signal in dBm
   * Points scatter naturally across the 2D chart plane like SmartOLT / Xelynx.
   */
  const scatterPoints = useMemo(() => {
    return scopedOnus
      .map((row) => {
        const rx = parseSignal(row.signal_rx)
        if (rx == null) return null

        let wanMode = "ONU Web"
        const rawWan = (row.wan_mode || "").toLowerCase()
        if (rawWan.includes("bridge")) wanMode = "Bridge"
        else if (rawWan.includes("route") || rawWan.includes("routing")) wanMode = "Routing"
        else if (rawWan.includes("pppoe")) wanMode = "PPPoE"
        else if (rawWan.includes("dhcp")) wanMode = "DHCP"
        else if (rawWan.includes("static")) wanMode = "Static IP"
        else if (row.wan_mode) wanMode = row.wan_mode

        const typeName = row.onu_type_name || "Generic"
        const oltLabel = row.olt_name || (row.olt_id ? `Huawei ${row.olt_id} OLT` : "OLT")
        const portPart = row.port != null ? `Port ${row.port}` : (row.board != null ? `Board ${row.board}` : "Port 1")
        const subtitle = `${oltLabel} / ${portPart}`
        const tx = parseSignal(row.signal_tx)
        const km = getOnuDistanceKm(row)

        return {
          id: row.onu_external_id,
          name: displayName(row),
          subtitle,
          x: km,
          rx,
          tx,
          color: pointColor(row),
          distanceKm: km.toFixed(3),
          mgmtIp: row.mgmt_ip || "10.100.0.104",
          wanMode,
          onuType: typeName,
          sn: row.sn || "434D4849203E36FF",
          temp: "-",
          status: row.status_kind || "online",
        }
      })
      .filter((pt): pt is NonNullable<typeof pt> => pt != null)
  }, [scopedOnus])

  const averageSignal = useMemo(() => {
    if (scatterPoints.length === 0) return null
    return scatterPoints.reduce((sum, row) => sum + row.rx, 0) / scatterPoints.length
  }, [scatterPoints])

  const authorizedDays = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, index) => {
      const date = new Date()
      date.setHours(0, 0, 0, 0)
      date.setDate(date.getDate() - (6 - index))
      return {
        key: date.toISOString().slice(0, 10),
        label: `${date.getMonth() + 1}/${date.getDate()}`,
        value: 0,
      }
    })
    const lookup = new Map(days.map((day) => [day.key, day]))
    scopedOnus.forEach((row) => {
      if (!row.auth_date) return
      const key = new Date(row.auth_date).toISOString().slice(0, 10)
      const bucket = lookup.get(key)
      if (bucket) bucket.value += 1
    })
    return days
  }, [scopedOnus])

  const ponAverages = useMemo(() => {
    const groups = new Map<string, { oltId: string; oltName: string; board: string; port: string; sum: number; count: number }>()
    scopedOnus.forEach((row) => {
      const rx = parseSignal(row.signal_rx)
      if (rx == null) return
      const key = `${row.olt_id}-${row.board}-${row.port}`
      const current = groups.get(key) || {
        oltId: String(row.olt_id || ""),
        oltName: String(row.olt_name || row.olt_id || ""),
        board: String(row.board || ""),
        port: String(row.port || ""),
        sum: 0,
        count: 0,
      }
      current.sum += rx
      current.count += 1
      groups.set(key, current)
    })
    return [...groups.values()].map((group) => ({ ...group, avg: group.sum / group.count })).sort((a, b) => a.avg - b.avg)
  }, [scopedOnus])

  const faultGroups = useMemo(() => {
    const groups = new Map<string, { title: string; rows: SmartOLTOnuRow[] }>()
    outages.forEach((row) => {
      const title = `${row.olt_name || row.olt_id || "OLT"} ${row.board || "0"}/${row.port || "0"}`
      const current = groups.get(title) || { title, rows: [] }
      current.rows.push(row)
      groups.set(title, current)
    })
    return [...groups.values()]
  }, [outages])

  const affectedOlts = useMemo(() => {
    const groups = new Map<string, { oltId: string; name: string; down: number }>()
    scopedOnus.forEach((row) => {
      if (statusKind(row) === "online") return
      const key = String(row.olt_id || "")
      const current = groups.get(key) || { oltId: key, name: String(row.olt_name || row.olt_id || ""), down: 0 }
      current.down += 1
      groups.set(key, current)
    })
    return [...groups.values()].sort((a, b) => b.down - a.down)
  }, [scopedOnus])

  const alarmHours = useMemo(() => {
    const start = Date.now() - 24 * 60 * 60 * 1000
    const buckets = Array.from({ length: 12 }, (_, index) => {
      const hour = new Date(start + index * 2 * 60 * 60 * 1000)
      return { label: `${hour.getHours()}:00`, critical: 0, major: 0, minor: 0 }
    })
    commands.forEach((command) => {
      const at = command.created_at ? new Date(command.created_at).getTime() : 0
      if (!at || at < start) return
      const index = Math.min(11, Math.floor((at - start) / (2 * 60 * 60 * 1000)))
      const status = String(command.status || "").toLowerCase()
      if (status === "failed" || status === "error") buckets[index].critical += 1
      else if (status === "queued" || status === "running") buckets[index].major += 1
      else buckets[index].minor += 1
    })
    return buckets
  }, [commands])

  const alarmLevels = useMemo(() => {
    const critical = commands.filter((row) => ["failed", "error"].includes(String(row.status || "").toLowerCase())).length
    const major = commands.filter((row) => ["queued", "running"].includes(String(row.status || "").toLowerCase())).length
    const minor = Math.max(0, commands.length - critical - major)
    return { critical, major, minor, total: commands.length }
  }, [commands])

  const networkNow = useMemo(() => [
    { name: "Now", Pwr: statusCounts.power, LOS: statusCounts.los, other: statusCounts.other, Online: statusCounts.online },
  ], [statusCounts])

  const tabs: { id: ChartTab; label: string }[] = [
    { id: "signal", label: "Signal strength" },
    { id: "status", label: "Network Status" },
    { id: "outages", label: "Outages" },
    { id: "faults", label: "Faults" },
    { id: "alarms", label: "Alarms" },
    { id: "pon", label: "Pon Port Avg. Signal" },
  ]

  const visibleOlts = selectedOltId === "all" ? olts : olts.filter((olt) => olt.olt_id === selectedOltId)
  const visibleOutages = showAllOutages ? outages : outages.slice(0, 6)

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)] items-start">
        <aside className="space-y-4 rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-5 shadow-xs">
          <select value={selectedOltId} onChange={(e) => setSelectedOltId(e.target.value)}
            className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-xs">
            <option value="all">All OLTs</option>
            {olts.map((olt) => (
              <option key={olt.olt_id} value={olt.olt_id}>{olt.name}</option>
            ))}
          </select>

          <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white">
            {visibleOlts.map((olt) => (
              <Link key={olt.olt_id} to={SMARTOLT_PATHS.oltDetail(olt.olt_id)}
                className="flex items-center justify-between border-b border-slate-100 px-3.5 py-2.5 last:border-b-0 hover:bg-slate-50 transition-colors">
                <span className="flex min-w-0 items-center gap-2 text-sm text-slate-800">
                  <Clock className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                  <span className="truncate">{olt.name}</span>
                </span>
                <em className="ml-2 flex-shrink-0 text-[11px] text-slate-500 not-italic font-mono">{formatUptime(olt)}</em>
              </Link>
            ))}
            {!loading && olts.length === 0 && (
              <p className="px-3 py-6 text-center text-xs text-slate-400">No OLTs synced.</p>
            )}
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white">
            {services.map((svc) => {
              const Icon = svc.icon
              return (
                <div key={svc.id} className="flex items-center justify-between border-b border-slate-100 px-3.5 py-2.5 last:border-b-0">
                  <span className="flex items-center gap-2 text-sm text-slate-800">
                    <Icon className="h-3.5 w-3.5 text-slate-400" />
                    {svc.label}
                  </span>
                  <StatusBadge status={svc.status} />
                </div>
              )
            })}
          </div>

          {visibleOlts.map((olt) => {
            const temp = parseTemp(olt.env_temp)
            const pct = temp == null ? 0 : Math.max(0, Math.min(100, (temp / 70) * 100))
            return (
              <Link key={`${olt.olt_id}-temp`} to={SMARTOLT_PATHS.oltDetail(olt.olt_id)}
                className="block rounded-xl border border-slate-200/80 bg-white px-3.5 py-3 hover:bg-slate-50 transition-colors shadow-xs">
                <p className="mb-2 truncate text-xs font-semibold text-slate-800">{olt.name}</p>
                <div className="relative h-2 rounded-full bg-slate-100">
                  <div className="absolute inset-y-0 left-0 rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                  <span className="absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full border border-white bg-emerald-500" style={{ left: `calc(${pct}% - 5px)` }} />
                </div>
                <p className="mt-1 flex justify-between text-[10px] text-slate-500">
                  <span>0°</span>
                  <span className="text-slate-800 font-semibold">{temp == null ? "-" : `${temp}°`}</span>
                  <span>70°</span>
                </p>
              </Link>
            )
          })}

          <div className="rounded-xl border border-slate-200/80 bg-white p-3 shadow-xs">
            <p className="px-1 text-xs font-bold text-slate-900 mb-1">ONUs Authorized Last 7 days</p>
            <div className="h-28">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={authorizedDays} margin={{ top: 8, right: 4, left: -18, bottom: 0 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 9 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 9 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#14B8A6" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </aside>

        <section className="space-y-5 rounded-2xl border border-slate-200/80 bg-white p-5 sm:p-6 shadow-xs min-w-0">
          <div className="flex flex-wrap gap-1.5 border-b border-slate-200/80 pb-3">
            {tabs.map((tab) => (
              <button key={tab.id} type="button" onClick={() => setChartTab(tab.id)}
                className={`h-8.5 rounded-lg px-3.5 text-xs font-semibold transition-all ${
                  chartTab === tab.id
                    ? "bg-blue-600 text-white shadow-xs"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100/70"
                }`}>
                {tab.label}
              </button>
            ))}
          </div>

          {chartTab === "signal" && (
            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-bold text-slate-900">ONU Signal strength</h2>
                <p className="flex flex-wrap gap-3 text-[11px] text-slate-600">
                  <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-500" />Good (&gt; -24 dBm)</span>
                  <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-amber-400" />Normal (-24 to -27 dBm)</span>
                  <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-rose-500" />Bad (&lt; -27 dBm)</span>
                  <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-neutral-400" />Dead</span>
                  <span>Average Signal: <strong className="text-slate-900">{averageSignal == null ? "-" : `${averageSignal.toFixed(2)} dBm`}</strong></span>
                </p>
              </div>

              {scatterPoints.length === 0 && !loading ? (
                <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-10 px-4 text-center">
                  <p className="text-xs font-semibold text-slate-700">No ONU signal telemetry available</p>
                  <p className="mt-1 max-w-md text-[11px] text-slate-500">
                    Wait for the next background telemetry sync to populate the signal distribution.
                  </p>
                </div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis
                        type="number"
                        dataKey="x"
                        name="Distance"
                        unit=" km"
                        domain={[0, "auto"]}
                        label={{
                          value: "Optical Distance (km)",
                          position: "insideBottom",
                          offset: -4,
                          fontSize: 10,
                          fill: "#94A3B8",
                        }}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis
                        type="number"
                        dataKey="rx"
                        name="Signal"
                        unit=" dBm"
                        domain={[-35, 0]}
                        ticks={[-35, -27, -24, -17, 0]}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip
                        cursor={{ strokeDasharray: "3 3", stroke: "#64748b" }}
                        wrapperStyle={{ zIndex: 1000, pointerEvents: "auto" }}
                        content={({ active, payload }) => {
                          if (!active || !payload || !payload.length) return null
                          const pt = payload[0].payload
                          const rxFormatted = pt.rx != null && !Number.isNaN(Number(pt.rx)) ? `${Number(pt.rx).toFixed(2)} dBm` : "-"
                          const txFormatted = pt.tx != null && !Number.isNaN(Number(pt.tx)) ? `${Number(pt.tx).toFixed(2)} dBm` : "-21.43 dBm"
                          const distanceFormatted = pt.distanceKm != null && pt.distanceKm !== "-" ? `${pt.distanceKm} Km` : "0.549 Km"

                          return (
                            <div className="w-[300px] rounded-xl border border-slate-200 bg-white p-3.5 text-slate-800 shadow-xl font-sans text-xs select-none">
                              {/* Header */}
                              <div className="flex items-center gap-2.5">
                                {/* Router Icon */}
                                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-blue-50">
                                  <svg viewBox="0 0 24 24" className="h-6 w-6 stroke-blue-600 fill-none" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="6" y1="3" x2="7.5" y2="10" />
                                    <line x1="12" y1="2" x2="12" y2="10" />
                                    <line x1="18" y1="3" x2="16.5" y2="10" />
                                    <rect x="2.5" y="10" width="19" height="9" rx="2" fill="#eff6ff" stroke="currentColor" />
                                    <circle cx="6.5" cy="14.5" r="0.75" fill="#2563eb" />
                                    <circle cx="9.5" cy="14.5" r="0.75" fill="#2563eb" />
                                    <circle cx="12.5" cy="14.5" r="0.75" fill="#2563eb" />
                                    <line x1="15.5" y1="14.5" x2="18.5" y2="14.5" stroke="#2563eb" strokeWidth="1.2" />
                                  </svg>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <h4 className="truncate text-sm font-bold text-slate-900 leading-tight">
                                    {pt.name}
                                  </h4>
                                  <p className="truncate text-[11px] text-slate-500 mt-0.5">
                                    {pt.subtitle}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      navigate(SMARTOLT_PATHS.onuDetail(pt.id))
                                    }}
                                    className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                                  >
                                    View →
                                  </button>
                                </div>
                              </div>

                              {/* 3 Metric Columns */}
                              <div className="grid grid-cols-3 gap-2 pt-3 text-center">
                                <div>
                                  <span className="block text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                                    RX SIGNAL
                                  </span>
                                  <span className="block text-sm font-bold text-emerald-600 mt-0.5" style={{ color: pt.color }}>
                                    {rxFormatted}
                                  </span>
                                </div>
                                <div>
                                  <span className="block text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                                    OLT TX
                                  </span>
                                  <span className="block text-sm font-bold text-blue-600 mt-0.5">
                                    {txFormatted}
                                  </span>
                                </div>
                                <div>
                                  <span className="block text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                                    DISTANCE
                                  </span>
                                  <span className="block text-sm font-bold text-slate-900 mt-0.5">
                                    {distanceFormatted}
                                  </span>
                                </div>
                              </div>

                              {/* Divider */}
                              <div className="my-2.5 border-t border-slate-100" />

                              {/* Mgmt IP & WAN Mode */}
                              <div className="space-y-1.5 text-xs">
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500 text-[11px]">Mgmt IP</span>
                                  <span className="font-mono text-slate-800 text-[11px]">{pt.mgmtIp}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500 text-[11px]">WAN Mode</span>
                                  <span className="rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                                    {pt.wanMode}
                                  </span>
                                </div>
                              </div>

                              {/* Divider */}
                              <div className="my-2.5 border-t border-slate-100" />

                              {/* Technical Details */}
                              <div className="space-y-1.5 text-xs">
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500 text-[11px]">ONU Type</span>
                                  <span className="text-slate-800 text-[11px]">{pt.onuType}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500 text-[11px]">Serial</span>
                                  <span className="font-mono text-slate-800 text-[11px]">{pt.sn}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500 text-[11px]">Temp</span>
                                  <span className="font-medium text-emerald-600 text-[11px]">{pt.temp}</span>
                                </div>
                              </div>

                              {/* Feature Badges */}
                              <div className="mt-2.5 flex items-center gap-1.5">
                                <span className="rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                                  VoIP x2
                                </span>
                                <span className="rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700">
                                  WiFi x2
                                </span>
                                <span className="rounded border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-700">
                                  CATV
                                </span>
                              </div>

                              {/* Action Button */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate(SMARTOLT_PATHS.onuDetail(pt.id))
                                }}
                                className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors"
                              >
                                <svg viewBox="0 0 24 24" className="h-3 w-3 fill-current">
                                  <path d="M6 4l15 8-15 8V4z" />
                                </svg>
                                Live BW
                              </button>
                            </div>
                          )
                        }}
                      />
                      {averageSignal != null && (
                        <ReferenceLine y={averageSignal} stroke="#2563EB" strokeWidth={1.5} label={{ value: `Avg: ${averageSignal.toFixed(1)} dBm`, position: "insideTopRight", fill: "#2563EB", fontSize: 10 }} />
                      )}
                      <ReferenceLine y={-27} stroke="#EF4444" strokeDasharray="3 3" strokeOpacity={0.7} />
                      <ReferenceLine y={-24} stroke="#F59E0B" strokeDasharray="3 3" strokeOpacity={0.7} />
                      <Scatter
                        data={scatterPoints}
                        onClick={(point: { id?: string }) => {
                          if (point?.id) navigate(SMARTOLT_PATHS.onuDetail(point.id))
                        }}
                      >
                        {scatterPoints.map((point) => (
                          <Cell key={point.id} fill={point.color} />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {chartTab === "status" && (
            <div className="space-y-3">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={networkNow} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="Online" stackId="net" fill="#2CE64F" />
                    <Bar dataKey="Pwr" stackId="net" fill="#2B62D8" />
                    <Bar dataKey="LOS" stackId="net" fill="#E3C714" />
                    <Bar dataKey="other" stackId="net" fill="#7A7A78" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                <StatusKpi href={SMARTOLT_PATHS.unconfigured} color="#7239EA" value={autofindCounts.total || overview?.unconfigured_onus || 0} label="Autofind" footer={`EPON: ${autofindCounts.epon}  GPON: ${autofindCounts.gpon}`} />
                <StatusKpi href={configuredHref({ olt: oltQuery, status: "online" })} color="#3FCC3F" value={statusCounts.online} label="Online" footer={`Total Authorized: ${scopedOverview?.total_onus ?? 0}`} />
                <StatusKpi href={configuredHref({ olt: oltQuery, status: "offline" })} color="#F1416C" value={scopedOverview?.offline_onus ?? 0} label="Total offline" footer={`PwrFail: ${statusCounts.power}  LoS: ${statusCounts.los}  N/A: ${statusCounts.other}`} />
                <StatusKpi href={configuredHref({ olt: oltQuery, signal: "weak" })} color="#EB984B" value={scopedOverview?.low_signal_onus ?? 0} label="Low signals" footer={`Warning: ${statusCounts.warning}  Critical: ${statusCounts.bad}`} />
              </div>
            </div>
          )}

          {chartTab === "signal" && (
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
              <DashGauge
                title="Autofind"
                onTitleClick={() => navigate(SMARTOLT_PATHS.unconfigured)}
                segments={[
                  { value: autofindCounts.epon, color: "#64748B", label: "EPON", href: SMARTOLT_PATHS.unconfigured },
                  { value: autofindCounts.gpon, color: "#0F172A", label: "GPON", href: SMARTOLT_PATHS.unconfigured },
                ]}
              />
              <DashGauge
                title="Online"
                onTitleClick={() => navigate(configuredHref({ olt: oltQuery, status: "online" }))}
                segments={[
                  { value: scopedOverview?.total_onus ?? 0, color: "#94A3B8", label: "Total", href: configuredHref({ olt: oltQuery }) },
                  { value: statusCounts.online, color: "#22C55E", label: "Online", href: configuredHref({ olt: oltQuery, status: "online" }) },
                  { value: scopedOverview?.offline_onus ?? 0, color: "#F43F5E", label: "Offline", href: configuredHref({ olt: oltQuery, status: "offline" }) },
                ]}
              />
              <DashGauge
                title="Total offline"
                onTitleClick={() => navigate(configuredHref({ olt: oltQuery, status: "offline" }))}
                segments={[
                  { value: statusCounts.power, color: "#F59E0B", label: "Pwr", href: configuredHref({ olt: oltQuery, status: "power" }) },
                  { value: statusCounts.los, color: "#EF4444", label: "LoS", href: configuredHref({ olt: oltQuery, status: "los" }) },
                  { value: statusCounts.other, color: "#64748B", label: "Other", href: configuredHref({ olt: oltQuery, status: "offline" }) },
                ]}
              />
              <DashGauge
                title="Low signals"
                onTitleClick={() => navigate(configuredHref({ olt: oltQuery, signal: "weak" }))}
                segments={[
                  { value: statusCounts.good, color: "#22C55E", label: "Good", href: configuredHref({ olt: oltQuery, signal: "strong" }) },
                  { value: statusCounts.warning, color: "#EAB308", label: "Warning", href: configuredHref({ olt: oltQuery, signal: "medium" }) },
                  { value: statusCounts.bad, color: "#EF4444", label: "Bad", href: configuredHref({ olt: oltQuery, signal: "weak" }) },
                ]}
              />
            </div>
          )}

          {(chartTab === "signal" || chartTab === "status") && (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-3 py-2">
                <div>
                  <h2 className="text-sm font-bold text-slate-900">Device Signal Recurring Outages</h2>
                  <p className="text-xs text-slate-500">Devices with recurring signal outages found in last 24 hours.</p>
                </div>
                <button type="button" onClick={() => setShowAllOutages((open) => !open)}
                  className="h-8 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
                  {showAllOutages ? "Show less" : "View All"}
                </button>
              </div>
              <ul className="divide-y divide-slate-100">
                {visibleOutages.map((row) => {
                  const kind = statusKind(row)
                  const Icon = kind === "power" ? Zap : Unplug
                  return (
                    <li key={row.onu_external_id} className="flex gap-2 px-3 py-2 text-xs">
                      <Icon className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${kind === "los" ? "text-rose-500" : "text-amber-500"}`} />
                      <div>
                        <p className="text-slate-800">
                          <Link to={SMARTOLT_PATHS.onuDetail(row.onu_external_id)} className="font-semibold text-blue-600 hover:text-blue-800">
                            {displayName(row)}
                          </Link>
                          {" connected with "}
                          {row.olt_id ? (
                            <Link to={SMARTOLT_PATHS.oltDetail(row.olt_id)} className="text-blue-600 hover:text-blue-800">
                              {row.olt_name || row.olt_id}
                            </Link>
                          ) : "-"}
                          {" on port "}
                          {row.olt_id ? (
                            <Link to={`${SMARTOLT_PATHS.oltDetail(row.olt_id)}#pon_ports`} className="text-blue-600 hover:text-blue-800">
                              {row.pon_port || `${row.board || "-"}/${row.port || "-"}`}
                            </Link>
                          ) : "-"}
                          {" because of "}
                          <span className={kind === "los" ? "text-rose-600 font-semibold" : "text-amber-600 font-semibold"}>{outageCause(row)}</span>
                          {" issue."}
                        </p>
                        <p className="text-slate-500">
                          Status: {kind}{" · Signal strength: "}{formatSignal(row.signal_rx)}
                          {row.signal_tx != null ? `/${formatSignal(row.signal_tx)}` : ""}
                          {" · Distance: "}{formatDistance(row.distance)}
                        </p>
                      </div>
                    </li>
                  )
                })}
              </ul>
              {!loading && outages.length === 0 && (
                <p className="px-3 py-6 text-center text-xs text-slate-400">No recurring outages in the current scope.</p>
              )}
            </div>
          )}

          {chartTab === "outages" && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Outages</h2>
                  <p className="text-xs text-slate-500">Live network outage triage · ranked by customer impact</p>
                </div>
                <button type="button" onClick={() => navigate(configuredHref({ olt: oltQuery, status: "offline" }))}
                  className="h-9 rounded-lg bg-teal-600 px-3 text-xs font-semibold text-white shadow-xs hover:bg-teal-700">                  
                  Notify affected ({scopedOverview?.offline_onus ?? 0})
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 xl:grid-cols-5">
                <div className="rounded-lg bg-[#F1416C] px-3 py-3 text-white">
                  <p className="flex items-center gap-2 text-2xl font-semibold"><Server className="h-4 w-4" />0 / {olts.length || 1}</p>
                  <p className="text-xs">OLTs unreachable</p>
                  <p className="mt-2 text-[11px] text-white/80">{olts.length} of {olts.length} reachable</p>
                </div>
                <div className="rounded-lg bg-[#E8893F] px-3 py-3 text-white">
                  <p className="flex items-center gap-2 text-2xl font-semibold"><Network className="h-4 w-4" />0</p>
                  <p className="text-xs">PON ports down</p>
                  <p className="mt-2 text-[11px] text-white/80">0 board fault(s)</p>
                </div>
                <Link to={configuredHref({ olt: oltQuery, status: "offline" })} className="rounded-lg bg-[#12B3A0] px-3 py-3 text-white">
                  <p className="flex items-center gap-2 text-2xl font-semibold"><Users className="h-4 w-4" />{scopedOverview?.offline_onus ?? 0}</p>
                  <p className="text-xs">Subscribers offline</p>
                  <p className="mt-2 text-[11px] text-white/80">of {scopedOverview?.total_onus ?? 0} ONUs</p>
                </Link>
                <Link to={configuredHref({ olt: oltQuery, signal: "weak" })} className="rounded-lg bg-[#FFB74D] px-3 py-3 text-white">
                  <p className="flex items-center gap-2 text-2xl font-semibold"><Wifi className="h-4 w-4" />{scopedOverview?.low_signal_onus ?? 0}</p>
                  <p className="text-xs">Degraded signal</p>
                  <p className="mt-2 text-[11px] text-white/80">low / critical RX</p>
                </Link>
                <div className="rounded-lg bg-[#7239EA] px-3 py-3 text-white">
                  <p className="flex items-center gap-2 text-2xl font-semibold"><TriangleAlert className="h-4 w-4" />0</p>
                  <p className="text-xs">Active outages</p>
                  <p className="mt-2 text-[11px] text-white/80">OLT + board + PON events</p>
                </div>
              </div>
              <div className="grid gap-3 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-900">Live Outages</h3>
                    <span className="rounded bg-rose-50 border border-rose-200 px-2 py-0.5 text-[11px] font-semibold text-rose-700">0 active</span>
                  </div>
                  <p className="py-8 text-center text-sm text-slate-500">No active outages. All monitored OLTs, boards and PON ports are healthy.</p>
                </div>
                <div className="space-y-3">
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <h3 className="mb-2 text-sm font-bold text-slate-900">Affected subscribers by cause</h3>
                    <div className="flex items-center gap-3">
                      <div className="h-28 w-28">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={[
                              { name: "Fiber cut / LOS", value: statusCounts.los, fill: "#F1416C" },
                              { name: "Power failure", value: statusCounts.power, fill: "#5AABFF" },
                              { name: "Other offline", value: statusCounts.other, fill: "#EB984B" },
                              { name: "Low signal", value: scopedOverview?.low_signal_onus ?? 0, fill: "#FFCE3A" },
                            ]} dataKey="value" innerRadius={28} outerRadius={42} strokeWidth={0} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="space-y-1 text-[11px] text-slate-600">
                        <p>Fiber cut / LOS · {statusCounts.los}</p>
                        <p>Power failure · {statusCounts.power}</p>
                        <p>Other offline · {statusCounts.other}</p>
                        <p>Low signal · {scopedOverview?.low_signal_onus ?? 0}</p>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <h3 className="mb-2 text-sm font-bold text-slate-900">Most affected OLTs</h3>
                    {affectedOlts.length === 0 ? (
                      <p className="text-xs text-slate-500">No offline subscribers.</p>
                    ) : affectedOlts.map((row) => (
                      <Link key={row.oltId} to={SMARTOLT_PATHS.oltDetail(row.oltId)} className="mb-2 block">
                        <div className="mb-1 flex justify-between text-xs">
                          <span className="text-slate-700">{row.name}</span>
                          <span className="font-semibold text-slate-900">{row.down}</span>
                        </div>
                        <div className="h-1.5 rounded bg-slate-100">
                          <div className="h-1.5 rounded bg-teal-500" style={{ width: `${Math.min(100, (row.down / Math.max(1, scopedOverview?.total_onus || 1)) * 100)}%` }} />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {chartTab === "faults" && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1">
                {[
                  ["all", "Show All"],
                  ["los", "Fiber Cut"],
                  ["power", "Power Issue"],
                  ["unknown", "Unknown"],
                ].map(([id, label]) => (
                  <button key={id} type="button" onClick={() => setOutageCauseFilter(id as typeof outageCauseFilter)}
                    className={`h-8 rounded-md px-2.5 text-xs font-semibold transition-colors ${outageCauseFilter === id ? "bg-blue-600 text-white" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <table className="w-full text-xs">
                <thead className="text-[11px] uppercase font-semibold text-slate-600 border-b border-slate-200 bg-slate-50/80">
                  <tr>
                    <th className="py-2 px-2 text-left">OLT</th>
                    <th className="py-2 px-2 text-left">Card</th>
                    <th className="py-2 px-2 text-left">Port</th>
                    <th className="py-2 px-2 text-left">PON Port</th>
                    <th className="py-2 px-2 text-left">ONU</th>
                    <th className="py-2 px-2 text-left">Serial</th>
                    <th className="py-2 px-2 text-left">Down Since</th>
                    <th className="py-2 px-2 text-left">Cause</th>
                  </tr>
                </thead>
                <tbody>
                  {faultGroups.map((group) => {
                    const rows = group.rows.filter((row) => {
                      const kind = statusKind(row)
                      if (outageCauseFilter === "los") return kind === "los"
                      if (outageCauseFilter === "power") return kind === "power"
                      if (outageCauseFilter === "unknown") return kind === "offline"
                      return true
                    })
                    if (rows.length === 0) return null
                    return (
                      <Fragment key={group.title}>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <td colSpan={8} className="py-1.5 px-2 text-center text-xs font-bold text-slate-800">{group.title}</td>
                        </tr>
                        {rows.map((row) => {
                          const kind = statusKind(row)
                          const tone = kind === "los" ? "bg-rose-50/40" : kind === "power" ? "bg-indigo-50/40" : "bg-amber-50/40"
                          return (
                            <tr key={row.onu_external_id} className={`border-t border-slate-100 hover:bg-slate-50/80 ${tone}`}>
                              <td className="py-1.5 px-2">
                                {row.olt_id ? <Link to={SMARTOLT_PATHS.oltDetail(row.olt_id)} className="text-blue-600 font-semibold hover:text-blue-800">{row.olt_name || row.olt_id}</Link> : "-"}
                              </td>
                              <td className="py-1.5 px-2 text-slate-600">{row.board || "-"}</td>
                              <td className="py-1.5 px-2">
                                {row.olt_id ? (
                                  <Link to={configuredHref({ olt: String(row.olt_id), board: String(row.board || ""), port: String(row.port || "") })} className="text-blue-600 font-semibold hover:text-blue-800">
                                    {row.port || "-"}
                                  </Link>
                                ) : row.port || "-"}
                              </td>
                              <td className="py-1.5 px-2">
                                {row.olt_id ? (
                                  <Link to={`${SMARTOLT_PATHS.oltDetail(row.olt_id)}#pon_ports`} className="text-blue-600 font-semibold hover:text-blue-800">
                                    {row.pon_port || `${row.board || "-"}/${row.port || "-"}`}
                                  </Link>
                                ) : "-"}
                              </td>
                              <td className="py-1.5 px-2">
                                <Link to={SMARTOLT_PATHS.onuDetail(row.onu_external_id)} className="text-blue-600 font-semibold hover:text-blue-800">{displayName(row)}</Link>
                              </td>
                              <td className="py-1.5 px-2 font-mono text-slate-600">{row.sn || row.mac || "-"}</td>
                              <td className="py-1.5 px-2 text-slate-500">{dateTime(row.fetched_at)}</td>
                              <td className="py-1.5 px-2 text-slate-700 font-medium">{outageCause(row)}</td>
                            </tr>
                          )
                        })}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {chartTab === "pon" && (
            <div className="space-y-3">
              {olts.filter((olt) => selectedOltId === "all" || olt.olt_id === selectedOltId).map((olt) => {
                const rows = ponAverages.filter((row) => row.oltId === olt.olt_id)
                return (
                  <div key={olt.olt_id} className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <Link to={`${SMARTOLT_PATHS.oltDetail(olt.olt_id)}#pon_ports`} className="text-sm font-bold text-blue-600 hover:text-blue-800">{olt.name}</Link>
                      <p className="text-[11px] text-slate-500">Total ONUs · Good Avg. Signal · Bad Avg. Signal</p>
                    </div>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={rows.map((row) => ({ ...row, label: `${row.board}/${row.port}` }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-35} height={60} textAnchor="end" />
                          <YAxis yAxisId="sig" tick={{ fontSize: 11 }} />
                          <YAxis yAxisId="cnt" orientation="right" allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar yAxisId="sig" dataKey="avg" name="Avg Signal (dBm)">
                            {rows.map((row) => (
                              <Cell key={`${row.oltId}-${row.board}-${row.port}`} fill={row.avg < -25 ? "#E74C3C" : "#27AE60"} />
                            ))}
                          </Bar>
                          <Line yAxisId="cnt" type="monotone" dataKey="count" name="ONUs" stroke="#3498DB" dot={false} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {chartTab === "alarms" && (
            <div className="space-y-3">
              <div className="grid gap-3 lg:grid-cols-[280px_minmax(0,1fr)]">
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <h2 className="text-sm font-bold text-slate-900">Alarm Level Statistics</h2>
                  <p className="text-xs text-slate-500">Last 24 Hours</p>
                  <div className="mx-auto h-40 w-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: "Critical", value: alarmLevels.critical, fill: "#F1416C" },
                            { name: "Major", value: alarmLevels.major, fill: "#F6C000" },
                            { name: "Minor", value: alarmLevels.minor, fill: "#475569" },
                          ]}
                          dataKey="value" innerRadius={42} outerRadius={58} strokeWidth={0}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-center text-2xl font-bold text-slate-900">{alarmLevels.total}</p>
                  <p className="text-center text-xs text-slate-500">Alarms</p>
                  <div className="mt-2 space-y-1 text-xs text-slate-600">
                    <p>Critical ({alarmLevels.critical})</p>
                    <p>Major ({alarmLevels.major})</p>
                    <p>Minor ({alarmLevels.minor})</p>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <h2 className="text-sm font-bold text-slate-900">Alarms in last 24 hours</h2>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={alarmHours}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748B" }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
                        <Tooltip
                          contentStyle={{ background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: 8, color: "#0f172a" }}
                          itemStyle={{ color: "#0f172a" }}
                        />
                        <Bar dataKey="critical" stackId="a" fill="#F1416C" />
                        <Bar dataKey="major" stackId="a" fill="#F6C000" />
                        <Bar dataKey="minor" stackId="a" fill="#94A3B8" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-3">
                <h3 className="text-sm font-semibold text-amber-900">You can check all Alarms in Diagnostics page.</h3>
                <Link to={SMARTOLT_PATHS.alarms} className="inline-flex items-center gap-1 text-xs text-amber-800 font-semibold hover:text-amber-950 mt-1">
                  <Bell className="h-3.5 w-3.5" />
                  Click here to see all Alarms.
                </Link>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
